//tapioca
// app/components/contact-us/Contact.tsx
"use client";
import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid, Button, Modal, IconButton } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import ContactForm from "../ContactForm";
import CloseIcon from "@mui/icons-material/Close";
import { renderWithLineBreaks } from "@/app/utils/renderWithLineBreaks";

const position = { lat: 13.7226668, lng: 100.5310694 };

// ===== ZH: Baidu Static Image (center + marker + zoom) with AK fallback → iframe =====
function BaiduStaticMap({
  lat,
  lng,
  title = "Poonphol Group",
  zoom = 16,
}: {
  lat: number;
  lng: number;
  title?: string;
  zoom?: number;
}) {
  const ak = process.env.NEXT_PUBLIC_BAIDU_AK;
  const width = 800;
  const height = 300;
  const markers = `${lng},${lat}|large,1`;
  const addressEn =
    "90/54-57 Sathorn Thani Bldg. 1, 19th Fl., North Sathorn Rd., Silom, Bangrak, Bangkok 10500, Thailand";

  if (!ak) {
    const iframeSrc =
      `https://api.map.baidu.com/marker?location=${lat},${lng}` +
      `&title=${encodeURIComponent(title)}` +
      `&content=${encodeURIComponent(addressEn)}` +
      `&output=html&coord_type=wgs84&zoom=${zoom}&autoOpen=false&src=webapp.Codediva.TapiocaSite`;

    return (
      <iframe
        src={iframeSrc}
        title={`${title} – Poonphol Group`}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        sandbox="allow-same-origin allow-scripts allow-popups allow-pointer-lock"
        style={{ border: 0, width: "100%", height: "100%", display: "block" }}
        aria-label="Poonphol Group"
      />
    );
  }

  const imgSrc =
    `https://api.map.baidu.com/staticimage/v2?ak=${ak}` +
    `&width=${width}&height=${height}` +
    `&center=${lng},${lat}` +
    `&zoom=${zoom}` +
    `&markers=${encodeURIComponent(markers)}` +
    `&coordtype=wgs84ll&scale=2`;

  const openMap = `https://map.baidu.com/poi/${encodeURIComponent(
    title
  )}/@${lat},${lng},${zoom}z?from=pc`;

  return (
    <a
      href={openMap}
      target="_blank"
      rel="noreferrer"
      aria-label="Open Poonphol Group Map"
      style={{ display: "block", width: "100%", height: "100%" }}
      title={title}
    >
      <img
        src={imgSrc}
        alt={title}
        loading="lazy"
        style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
      />
    </a>
  );
}

// ===== Map Loader (ZH -> Baidu Static/iframe, EN -> Google iframe) =====
function LazyMap({ lat, lng }: { lat: number; lng: number }) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [show, setShow] = React.useState(false);
  const locale = useLocale();

  React.useEffect(() => {
    if (!ref.current || show) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [show]);

  const isZh = locale === "zh";
  const zoom = 16;

  const gmapSrc = React.useMemo(() => {
    const lang = "en";
    const q = encodeURIComponent("Poonphol Group");
    return `https://www.google.com/maps?q=${q}&z=${zoom}&hl=${lang}&gl=US&output=embed`;
  }, [zoom]);

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 5,
        overflow: "hidden",
      }}
    >
      {!show && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, #e8efe8, #d6e6d5 40%, #cfe0cf)",
          }}
        />
      )}

      {show &&
        (isZh ? (
          <BaiduStaticMap lat={lat} lng={lng} zoom={zoom} />
        ) : (
          <iframe
            src={gmapSrc}
            title="Poonphol Group"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-pointer-lock"
            style={{ border: 0, width: "100%", height: "100%", display: "block" }}
            aria-label="Google Map"
          />
        ))}
    </Box>
  );
}

// ===== Directions Button (EN → Google, ZH → Baidu with latlng + name) =====
function DirectionsButtons({ lat, lng }: { lat: number; lng: number }) {
  const locale = useLocale();
  const isZh = locale === "zh";
  const open = (url: string) => window.open(url, "_blank", "noopener,noreferrer");

  const makeBaiduUrl = (oLat?: number, oLng?: number) => {
    const destName = encodeURIComponent("Poonphol Group");
    if (oLat != null && oLng != null) {
      return (
        `https://map.baidu.com/direction?origin=latlng:${oLat},${oLng}|name:${encodeURIComponent("我的位置")}` +
        `&destination=latlng:${lat},${lng}|name:${destName}&mode=driving&coord_type=wgs84`
      );
    }
    return (
      `https://map.baidu.com/direction?origin=${encodeURIComponent("我的位置")}` +
      `&destination=latlng:${lat},${lng}|name:${destName}&mode=driving&coord_type=wgs84`
    );
  };

  const handleClick = () => {
    if (isZh) {
      if (!navigator.geolocation) return open(makeBaiduUrl());
      navigator.geolocation.getCurrentPosition(
        (pos) => open(makeBaiduUrl(pos.coords.latitude, pos.coords.longitude)),
        () => open(makeBaiduUrl()),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    } else {
      const makeGoogleUrl = (oLat?: number, oLng?: number) => {
        if (oLat != null && oLng != null)
          return `https://www.google.com/maps/dir/?api=1&origin=${oLat},${oLng}&destination=${lat},${lng}&travelmode=driving`;
        return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
      };
      if (!navigator.geolocation) return open(makeGoogleUrl());
      navigator.geolocation.getCurrentPosition(
        (pos) => open(makeGoogleUrl(pos.coords.latitude, pos.coords.longitude)),
        () => open(makeGoogleUrl()),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    }
  };

  return (
    <Box sx={{
      mt: 1
    }}>
      <Button variant="outlined" size="small" onClick={handleClick}>
        {isZh ? "从当前位置导航（百度）" : "Get directions from here (Google)"}
      </Button>
    </Box>
  );
}

export default function Contact() {
  const t = useTranslations("Contact");

  const [openWeChatModal, setOpenWeChatModal] = React.useState(false);
  const handleWeChatModalOpen = () => setOpenWeChatModal(true);
  const handleWeChatModalClose = () => setOpenWeChatModal(false);

  // ✅ เพิ่ม state สำหรับ WhatsApp Modal
  const [openWhatsAppModal, setOpenWhatsAppModal] = React.useState(false);
  const handleWhatsAppModalOpen = () => setOpenWhatsAppModal(true);
  const handleWhatsAppModalClose = () => setOpenWhatsAppModal(false);

  return (
    <Box className="contact-wrap" sx={{
      py: { xs: 5, lg: 10 }
    }}>
      <Container className="container">
        <Grid container spacing={{ xs: 3, md: 10 }} sx={{
          alignItems: "center"
        }}>
          {/* ===== Left: Info ===== */}
          <Grid size={{ xs: 12, md: 5 }} className="contact-info">
            <Typography
              component="h1"
              variant="h1"
              data-aos="fade-up"
              sx={{
                mb: 2,
                whiteSpace: { xs: "normal", lg: "pre-line" }
              }}>
              {t("title")}
            </Typography>
            <Typography
              variant="subtitle1"
              data-aos="fade-up"
              sx={{
                mb: 2,
                fontWeight: 600,
                fontSize: "1.25rem"
              }}>
              {t("subtitle")}
            </Typography>
            <Typography variant="body1" data-aos="fade-up" sx={{
              mb: 3
            }}>
              {t("address")}
            </Typography>
            <Typography
              variant="body1"
              data-aos="fade-up"
              sx={{
                mb: 3,
                fontWeight: 600
              }}>
              {t("open")}
            </Typography>
            <Typography
              variant="body1"
              data-aos="fade-up"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 2
              }}>
              <Image
                src="/assets/email-icon-white.svg"
                width={30}
                height={30}
                title="Poonphol Tapioca email"
                alt="Poonphol Tapioca email"
              />
              <Link href={`mailto:${t("email")}`} passHref>
                {t("email")}
              </Link>
            </Typography>
            <Typography
              variant="body1"
              data-aos="fade-up"
              sx={{
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 2
              }}>
              <Image
                src="/assets/phone-icon-white.svg"
                width={30}
                height={30}
                title="Poonphol Tapioca phone"
                alt="Poonphol Tapioca phone"
              />
              <Link href={`tel:${t("telRaw")}`} passHref>
                {t("phone")}
              </Link>
            </Typography>

            {/* ==== Social icons ==== */}
            <Box
              data-aos="fade-up"
              className="social-icons"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2
              }}>
              {/* LINE */}
              <a
                href="https://line.me/ti/p/A4gxe3dGz3"
                target="_blank"
                rel="noopener noreferrer nofollow"
                aria-label="Open LINE"
                style={{ display: "inline-flex" }}
              >
                <Image
                  src="/assets/line.svg"
                  width={80}
                  height={80}
                  title="Poonphol Tapioca line"
                  alt="Poonphol Tapioca line"
                />
              </a>

              {/* WeChat → Modal */}
              <IconButton
                onClick={handleWeChatModalOpen}
                aria-label="Open WeChat QR Code"
                color="primary"
                sx={{ width: 80, height: 80 }}
              >
                <Image
                  src="/assets/wechat.svg"
                  width={80}
                  height={80}
                  title="Poonphol Tapioca wechat"
                  alt="Open WeChat QR Code"
                />
              </IconButton>

              {/* WhatsApp → Modal เหมือน WeChat */}
              <IconButton
                onClick={handleWhatsAppModalOpen}
                aria-label="Open WhatsApp QR Code"
                color="primary"
                sx={{ width: 80, height: 80 }}
              >
                <Image
                  src="/assets/whatsapp.svg"
                  width={80}
                  height={80}
                  title="whatsapp"
                  alt="Open WhatsApp QR Code"
                />
              </IconButton>
            </Box>

            <Box
              data-aos="fade-up"
              sx={{
                mt: 3,
                height: 235,
                width: "100%",
                borderRadius: 5,
                overflow: "hidden"
              }}>
              <LazyMap lat={position.lat} lng={position.lng} />
            </Box>
            {/* <DirectionsButtons lat={position.lat} lng={position.lng} /> */}
          </Grid>

          {/* ===== Right: Form ===== */}
          <Grid size={{ xs: 12, md: 7 }} data-aos="fade-up">
            <ContactForm sx={{ pb: { xs: 5, md: 12 } }} />
          </Grid>
        </Grid>
      </Container>
      {/* WeChat QR Code Modal */}
      <Modal
        open={openWeChatModal}
        onClose={handleWeChatModalClose}
        aria-labelledby="wechat-qr-modal-title"
        aria-describedby="wechat-qr-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "70%", md: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2
            }}>
            <IconButton
              aria-label="close"
              onClick={handleWeChatModalClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            id="wechat-qr-modal-description"
            sx={{ mt: 2, mb: 1 }}
          >
            {renderWithLineBreaks(t("add_wechat"))}
          </Typography>
          <Box
            component="div"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 2,
            }}
          >
            <Image
              src="/assets/wechat-qrcode.jpg"
              alt="WeChat QR Code"
              width={200}
              height={200}
              style={{ objectFit: "contain" }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1
            }}>
            {renderWithLineBreaks(t("wechat_id"))}
          </Typography>
        </Box>
      </Modal>
      {/* WhatsApp QR Code Modal */}
      <Modal
        open={openWhatsAppModal}
        onClose={handleWhatsAppModalClose}
        aria-labelledby="whatsapp-qr-modal-title"
        aria-describedby="whatsapp-qr-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "70%", md: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2
            }}>
            <IconButton
              aria-label="close"
              onClick={handleWhatsAppModalClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            id="whatsapp-qr-modal-description"
            sx={{ mt: 2, mb: 1 }}
          >
            {renderWithLineBreaks(t("add_whatsapp"))}
          </Typography>
          <Box
            component="div"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 2,
            }}
          >
            <Image
              src="/assets/whatsapp-qrcode.jpg" // ใส่ไฟล์ QR ของ WhatsApp ไว้ที่นี่
              alt="Poonphol Tapioca WhatsApp QR Code"
              width={200}
              height={200}
              style={{ objectFit: "contain" }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1
            }}>
            {renderWithLineBreaks(t("whatsapp_id"))}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
}
